import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import {
  Box,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@/shared/ui';

import { DateTextVariants, useDateText } from '@/features/_shared/user';
import copyToClipboard from '@/shared/lib/copyToClipboard';
import Button from '@/shared/ui/primitives/Button';

import ConfirmDialog from './ConfirmDialog';
import useApiKeyTable from './useApiKeyTable';

import type { ApiKeyOutput } from '../api';

const StyledTableCell = styled(TableCell)(() => ({
  paddingRight: 30,
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  height: '50px',
  paddingRight: 1,
  borderBottom: `0.5px solid ${theme.palette.gx.neutral}`,
  '&:last-child tr': { border: 0, borderBottom: 0 }
}));

const StyledHeaderRow = styled(TableRow)(({ theme }) => ({
  height: '50px',
  borderBottom: `1px solid ${theme.palette.gx.text}`
}));

const emptyMessage = (
  <Box sx={{ display: 'flex', width: '100%' }}>
    <Typography
      align='center'
      sx={{ width: '100%', my: 5 }}
    >
      No token generated yet.
    </Typography>
  </Box>
);

const ApiKeyTable = ({ apiKeys }: { apiKeys: ApiKeyOutput[] }) => {
  const { open, setOpen, openConfirmModal, deleteKey } = useApiKeyTable();
  const { formatDateString } = useDateText();

  return (
    <Box
      sx={() => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      })}
    >
      <ConfirmDialog
        deleteCallback={deleteKey}
        open={open}
        setOpen={setOpen}
      />
      {apiKeys.length ? (
        <TableContainer component={Paper}>
          <Table
            aria-label='api keys table'
            size='small'
            sx={{ minWidth: 600 }}
          >
            <TableHead>
              <StyledHeaderRow>
                <StyledTableCell>
                  <Typography variant='body2'>Name</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Access Token</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Creation Date</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2'>Last Used</Typography>
                </StyledTableCell>
                <StyledTableCell />
              </StyledHeaderRow>
            </TableHead>
            <TableBody>
              {[...apiKeys]
                .toSorted((a, b) => b.creationDate.localeCompare(a.creationDate))
                .map(apiKey => (
                  <StyledTableRow
                    key={apiKey.label}
                    sx={{ '&:last-child': { borderBottom: 0 } }}
                  >
                    <StyledTableCell
                      scope='row'
                      sx={{ width: '25%' }}
                    >
                      <Typography
                        sx={{ maxWidth: '200px', overflow: 'hidden' }}
                        variant='body2'
                      >
                        {apiKey.label}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '25%' }}>
                      {apiKey.prefixedRawApiKey && (
                        <Box
                          sx={{
                            display: 'flex',
                            maxWidth: 210,
                            overflowX: 'auto',
                            overflowY: 'hidden'
                          }}
                        >
                          <Typography
                            sx={{ mr: 0.5 }}
                            variant='body2'
                          >
                            {`${apiKey.prefixedRawApiKey.substring(0, 9)}**********`}
                          </Typography>
                          <Button
                            size='small'
                            startIcon={<ContentCopyOutlined />}
                            variant='text'
                            onClick={() =>
                              copyToClipboard(
                                apiKey.prefixedRawApiKey ?? '',
                                'Api key copied to clipboard!'
                              )
                            }
                          >
                            Copy
                          </Button>
                        </Box>
                      )}
                      {!apiKey.prefixedRawApiKey && (
                        <Typography
                          sx={{ maxWidth: '200px', overflowX: 'auto', overflowY: 'hidden' }}
                          variant='body2'
                        >
                          {`**********${apiKey.lastThreeCharacters}`}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '15%' }}>
                      {formatDateString(apiKey.creationDate, DateTextVariants.FULL)}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '30%' }}>
                      {formatDateString(apiKey.lastUsed ?? '---', DateTextVariants.WITH_TIME)}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '15%' }}>
                      <Button
                        color='secondary'
                        size='small'
                        variant='text'
                        onClick={() => {
                          openConfirmModal(apiKey);
                        }}
                      >
                        Remove
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        emptyMessage
      )}
    </Box>
  );
};

export default ApiKeyTable;
